/**
 * Mantener viva la sesión mientras el panel está en uso. El token de sesión dura dos horas
 * (`tokenExpiration` en el núcleo) y antes nadie lo renovaba: quien pasaba dos horas escribiendo
 * una página perdía lo escrito al pulsar «Guardar».
 *
 * - Cada minuto se mira; si la pestaña está a la vista y hace 20 minutos de la última
 *   renovación, se renueva. Una pestaña olvidada en segundo plano no alarga la sesión sola.
 * - Al volver a la pestaña, si hace más de 5 minutos, se renueva enseguida; y si la sesión ya
 *   se ha ido, se pide volver a entrar **antes** de que la persona siga escribiendo.
 * - Si el núcleo dice que esta sesión no se renueva (la visita del equipo a la cuenta de un
 *   cliente dura una hora y no se alarga), se deja de intentar.
 *
 * La lógica va aparte de React para poder probarla con un reloj de mentira.
 */
export const CADA_MS = 20 * 60_000
export const AL_VOLVER_MS = 5 * 60_000

/** Lo que responde un intento: renovada, ya no hay sesión, esta sesión no se renueva, o no se sabe (la red). */
export type ResultadoDeRenovar = 'fallo' | 'no-renovable' | 'ok' | 'sin-sesion'

export type Renovador = {
  /** Cada minuto. */
  alTic: () => Promise<void>
  /** Al volver a la pestaña. */
  alVolver: () => Promise<void>
  readonly apagado: boolean
}

export const crearRenovador = (o: {
  renovar: () => Promise<ResultadoDeRenovar>
  alPerderla: () => void
  ahora?: () => number
  visible?: () => boolean
}): Renovador => {
  const ahora = o.ahora ?? Date.now
  const visible = o.visible ?? (() => true)
  let ultima = ahora()
  let apagado = false
  let enCurso: null | Promise<void> = null

  const intentar = (): Promise<void> => {
    if (apagado) return Promise.resolve()
    enCurso ??= (async () => {
      const resultado = await o.renovar()
      if (resultado === 'no-renovable') apagado = true
      // Con «fallo» (la red) se vuelve a intentar en el siguiente minuto; con los demás, no hasta el plazo.
      if (resultado !== 'fallo') ultima = ahora()
      if (resultado === 'sin-sesion') o.alPerderla()
    })().finally(() => {
      enCurso = null
    })
    return enCurso
  }

  return {
    alTic: () => (visible() && ahora() - ultima >= CADA_MS ? intentar() : Promise.resolve()),
    alVolver: () => (ahora() - ultima >= AL_VOLVER_MS ? intentar() : Promise.resolve()),
    get apagado() {
      return apagado
    },
  }
}
